import { API } from './api';

export const FIND_CATEGORIES = ['plants', 'fruit', 'nuts', 'mushrooms', 'animals', 'insects', 'other'];
export function findCategoryLabels(da = false) {
    return da ? { plants: 'Planter, urter & grønt', fruit: 'Frugt & bær', nuts: 'Nødder', mushrooms: 'Svampe', animals: 'Dyr (egen observation)', insects: 'Insekter (egen observation)', other: 'Ukendt / andet' }
        : { plants: 'Plants, herbs & vegetables', fruit: 'Fruit & berries', nuts: 'Nuts', mushrooms: 'Mushrooms', animals: 'Animals (own observation)', insects: 'Insects (own observation)', other: 'Unknown / other' };
}

export function findIdentificationStatus(result) {
    if (!result || result.kind === 'other' || !result.kind) return 'unidentified';
    if (['unidentified', 'uncertain', 'suggested', 'unverified'].includes(result.identification_status)) return result.identification_status;
    const name = String(result.common_name || '').trim().toLowerCase();
    if (!name || ['unknown', 'ukendt', 'unidentified'].includes(name)) return 'unidentified';
    const confidence = result.confidence;
    return typeof confidence === 'number' && Number.isFinite(confidence) && confidence >= 80 ? 'suggested' : 'uncertain';
}

export function findIdentificationLabel(status, da = false) {
    const labels = da ? { unidentified: 'Ikke identificeret', uncertain: 'Usikkert artsforslag', suggested: 'Artsforslag — ikke verificeret', unverified: 'Egen observation — ikke verificeret' }
        : { unidentified: 'Not identified', uncertain: 'Uncertain species suggestion', suggested: 'Species suggestion — not verified', unverified: 'Own observation — not verified' };
    return labels[status] || labels.unverified;
}
export const COUNTRY_CODES = 'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'.split(' ').filter((code, index, codes) => codes.indexOf(code) === index);

export function countryName(code, locale = 'en') {
    try { return new Intl.DisplayNames([locale], { type: 'region' }).of(code) || code; }
    catch { return code; }
}

export function findCategory(species, kind = 'wild') {
    const value = species?.category || species?.kind || kind;
    if (['mushroom', 'fungus', 'mushrooms'].includes(value)) return 'mushrooms';
    if (['berry', 'fruit'].includes(value)) return 'fruit';
    if (['nut', 'nuts'].includes(value)) return 'nuts';
    if (['animal', 'animals', 'bird', 'mammal'].includes(value)) return 'animals';
    if (['insect', 'insects'].includes(value)) return 'insects';
    if (['other', 'unknown', 'unidentified'].includes(value)) return 'other';
    if (species?.kind && !['wild', 'plant', 'plants', 'herb', 'spice', 'vegetable', 'tree', 'shrub', 'flower', 'grass', 'houseplant'].includes(value)) return 'other';
    if (['hassel', 'bog', 'aegte-kastanje', 'valnod'].includes(species?.id)) return 'nuts';
    if (['hyld', 'brombaer', 'hindbaer', 'blaabaer', 'skovjordbaer', 'slaaen', 'havtorn', 'hyben', 'hvidtjorn', 'pawpaw', 'amerikansk-persimmon', 'prickly-pear'].includes(species?.id)) return 'fruit';
    return 'plants';
}

export function findPhotoEndpoint(find, index = 0) {
    if (!find?.id || !Number.isInteger(index) || index < 0 || index >= (find.photo_count || find.photo_urls?.length || find.photo_paths?.length || (find.photo_path ? 1 : 0))) return '';
    const path = `/finds/${encodeURIComponent(find.id)}/photos/${index}`;
    const adminPath = `/finds/admin/${encodeURIComponent(find.id)}/photos/${index}`;
    const supplied = find.photo_urls?.[index];
    // Only these relative routes for this find and angle can receive credentials.
    if (supplied === `/api${adminPath}`) return adminPath;
    if (supplied && supplied !== `/api${path}`) return '';
    return path;
}

export function findPhotoUrl(find, index = 0) {
    const endpoint = findPhotoEndpoint(find, index);
    return endpoint ? `${API}${endpoint}` : '';
}

export function locationErrorMessage(error, da = false) {
    if (error?.code === 1) return da
        ? 'Placering er blokeret. Tillad placering i browserens eller telefonens indstillinger, eller indtast koordinater nedenfor.'
        : 'Location is blocked. Allow location in your browser or phone settings, or enter coordinates below.';
    if (error?.code === 3) return da
        ? 'Det tog for lang tid at finde din placering. Prøv igen udendørs, eller indtast koordinater nedenfor.'
        : 'Finding your location timed out. Try again outdoors, or enter coordinates below.';
    return da ? 'Placeringen kunne ikke findes. Prøv igen, eller indtast koordinater nedenfor.'
        : 'Your location could not be found. Try again, or enter coordinates below.';
}

export function cameraErrorMessage(error, da = false) {
    if (['NotAllowedError', 'PermissionDeniedError', 'SecurityError'].includes(error?.name)) return da
        ? 'Kameraet er blokeret. Tillad kamera i browserens eller telefonens indstillinger, eller brug kameraknappen eller et foto fra dit album.'
        : 'Camera access is blocked. Allow camera in your browser or phone settings, or use the camera button or a photo from your album.';
    if (['NotReadableError', 'TrackStartError'].includes(error?.name)) return da
        ? 'Kameraet er optaget af en anden app. Luk den og prøv igen, eller vælg et foto.'
        : 'Another app is using the camera. Close it and try again, or choose a photo.';
    return da ? 'Livekameraet er ikke tilgængeligt her. Brug kameraknappen eller vælg et foto fra dit album.'
        : 'The live camera is unavailable here. Use the camera button or choose a photo from your album.';
}
